import { createAction } from '@reduxjs/toolkit';
import { WebSocketController } from '@x-guard/xgac-ts-ws-client';
import { AlarmProfessionalResponder, AlarmProfessionalResponderStatus } from '@x-guard/xgac-types/xgac';
import {
  api,
  asyncPollableDefaults,
  createSlice,
  createThunk, withPollableFulfilled,
  withPollablePending, withPollableRejected, withPollableRetrying,
} from 'lib';
import { maxBy, uniqBy } from 'lodash';
import {
  AsyncStatus, PopulatedAlarmProfessionalResponder, ProfessionalResponseState, RS,
  UsedCustomer,
} from 'types';
import { ackedSelector } from '../../ack/exports';

const name = 'details/response/professional';

export type StartProfessionalResponseArgs = {
  controller: AbortController;
  alarmId: string;
};

export type CancelProfessionalResponseArgs = {
  controller: AbortController;
  alarmId: string;
  aprId: string;
};

export type FetchProfessionalResponseArgs = {
  ws: WebSocketController;
  controller: AbortController;
  alarmId: string;
  customer: UsedCustomer;
};

export const createResponder = createAction<PopulatedAlarmProfessionalResponder>('details/response/professional/create');
export const updateResponder = createAction<AlarmProfessionalResponder>('details/response/professional/update');

export const startProfessionalResponse = createThunk<unknown, StartProfessionalResponseArgs>({
  name: `${name}/start`,
  handler: async ({ alarmId }) => {

    const { data } = await api.post(`/alarms/${alarmId}/alarm-professional-responders`, {
      type: 'alarmProfessionalResponder',
      data: {},
    });

    return data;

  },
});

export const cancelProfessionalResponse = createThunk<unknown, CancelProfessionalResponseArgs>({
  name: `${name}/cancel`,
  handler: async ({ alarmId, aprId }) => {

    const { data } = await api.patch(`/alarms/${alarmId}/alarm-professional-responders/${aprId}`, {
      status: 'canceled',
    });

    return data;

  },
});

export const fetchProfessionalResponse = createThunk<PopulatedAlarmProfessionalResponder[], FetchProfessionalResponseArgs>({
  name: `${name}/fetch`,
  handler: async ({
    alarmId,
  }) => {

    const { data } = await api.get<{ result: PopulatedAlarmProfessionalResponder[] }>(`/alarms/${alarmId}/alarm-professional-responders`);

    return data.result;

  },
});

export const professionalResponse = createSlice<ProfessionalResponseState, typeof name>({
  name,
  initialState: {
    ...asyncPollableDefaults,
    startStatus: AsyncStatus.Idle,
    cancelStatus: AsyncStatus.Idle,
    value: [],
  },
  extraReducers: (builder) => builder
    .addCase(createResponder, (state, { payload }) => {

      state.value = uniqBy([payload, ...state.value], '_id');

    })
    .addCase(updateResponder, (state, { payload }) => {

      const item = state.value.find(({ _id }) => _id === payload._id);

      if (!item) return;

      const newItem: PopulatedAlarmProfessionalResponder = {
        ...payload,
        staticResponder: item.staticResponder,
      };

      state.value = uniqBy([newItem, ...state.value], '_id');

    })
    .addCase(startProfessionalResponse.pending, (state) => {

      state.startStatus = AsyncStatus.Pending;

    })
    .addCase(startProfessionalResponse.fulfilled, (state) => {

      state.startStatus = AsyncStatus.Fulfilled;

    })
    .addCase(startProfessionalResponse.rejected, (state) => {

      state.startStatus = AsyncStatus.Rejected;

    })
    .addCase(fetchProfessionalResponse.pending, withPollablePending)
    .addCase(fetchProfessionalResponse.fulfilled, withPollableFulfilled)
    .addCase(fetchProfessionalResponse.retrying, withPollableRetrying)
    .addCase(fetchProfessionalResponse.rejected, withPollableRejected),
});

export const professionalResponderSelector = (state: RS) => {

  const list = ackedSelector(state)
    ? state.details.value?.alarm?.meta?.frozenInfo?.professionalResponders ?? []
    : state.details.response.professional.value;

  return maxBy(list, (responder) => new Date(responder.updatedAt)) ?? null;

};

export const startResponseStatusSelector = (state: RS) => state.details.response.professional.startStatus;
export const cancelResponseStatusSelector = (state: RS) => state.details.response.professional.cancelStatus;

export const inProgressSelector = (state: RS) => {

  if (!ackedSelector(state)) return false;

  const responder = professionalResponderSelector(state);

  if (responder) {

    return responder.status !== AlarmProfessionalResponderStatus.Canceled;

  }

  const status = startResponseStatusSelector(state);

  return status.fulfilled || status.pending;

};
